import type { Localized } from '../../i18n'
import type { EdoRequest } from '../../lib/types'

/** Empty strings explicitly clear optional draft fields; omission preserves them. */
export interface TerminationInput {
  terminationDate?: string
  terminationReasonId?: string
  reason?: string
}
export interface TerminationDraftInput extends TerminationInput { employmentId?: string }
export interface TerminationReferences {
  companyId: string
  source: string
  employeeId: string
  employeeName: string
  /** name — код 1С (уходит в ссылку), text — формулировка статьи из 1С. */
  reasons: { id: string; name: string; text?: string }[]
  /** Основание по умолчанию — ст. 69.1 (сотрудник его не выбирает, ответ аналитика 07.10.2026). */
  basis?: { id: string; name: string; text?: string } | null
  /** Самая ранняя дата увольнения для новой заявки: месяц от сегодняшней подачи. */
  earliestDate?: string
  blockers: string[]
  canSubmit: boolean
}

/** Причины ухода для Exit-интервью — зеркало бэкендового EXIT_REASONS (termination-route.ts). */
export const EXIT_REASONS = [
  { value: 'salary', label: { ru: 'Заработная плата', en: 'Salary', az: 'Əmək haqqı' } },
  { value: 'career', label: { ru: 'Карьерный рост и развитие', en: 'Career growth', az: 'Karyera inkişafı' } },
  { value: 'manager', label: { ru: 'Отношения с руководителем', en: 'Relations with manager', az: 'Rəhbərlə münasibət' } },
  { value: 'conditions', label: { ru: 'Условия труда и нагрузка', en: 'Working conditions and workload', az: 'İş şəraiti və yüklənmə' } },
  { value: 'relocation_family', label: { ru: 'Переезд или семейные обстоятельства', en: 'Relocation or family', az: 'Köçmə və ya ailə vəziyyəti' } },
  { value: 'other_employer', label: { ru: 'Переход к другому работодателю', en: 'Another employer', az: 'Başqa işəgötürənə keçid' } },
  { value: 'study', label: { ru: 'Учёба', en: 'Study', az: 'Təhsil' } },
  { value: 'other', label: { ru: 'Другое', en: 'Other', az: 'Digər' } },
] as const
export type ExitReason = (typeof EXIT_REASONS)[number]['value']
export interface ExitInterviewInput {
  refused?: boolean
  reason?: ExitReason
  comment?: string
  uploadId?: string
}
/** Результаты интервью в карточке — сервер отдаёт их только HR компании, CEO и admin. */
export interface ExitInterviewView {
  refused: boolean
  reason: ExitReason | null
  comment: string | null
  createdAt: string
  by?: { id: string; fullName: string } | null
}
export function isPreparedTermination(card: Pick<EdoRequest, 'type' | 'payload'>): boolean {
  return (card.type === 'TERMINATION' || card.type === 'TERMINATION_EMPLOYER') &&
    (card.payload as unknown as { terminationContractVersion?: string }).terminationContractVersion === '2026-10-05'
}
/** Неподанный черновик увольнения: маршрута ещё нет (после подачи он виден как у прочих процессов). */
export function isLocalTerminationDraft(card: Pick<EdoRequest, 'type' | 'payload' | 'status'>): boolean {
  return card.status === 'DRAFT' && isPreparedTermination(card)
}
export function canEditTerminationDraft(card: EdoRequest, userId?: string): boolean {
  return !!userId && (card.initiator?.id ?? card.initiatorId) === userId && card.type === 'TERMINATION' &&
    card.status === 'DRAFT' && isPreparedTermination(card) &&
    !card.isSigned && !card.processInstanceId && !Object.keys(card.result ?? {}).length
}

// ── Увольнение по инициативе работодателя (объединённая форма HR FM, ответы HR 30.10.2026) ──
// Словари — зеркало бэкендового termination-employer.ts: карточке нужны подписи и тогда, когда
// справочник по сотруднику недоступен (его отдают только подающему руководителю).

export type EmployerCategory = 'manager' | 'specialist' | 'worker'
export interface FormLabel { key: string; label: Localized }

export const EMPLOYER_CATEGORIES: { key: EmployerCategory; label: Localized }[] = [
  { key: 'manager', label: { ru: 'Руководитель', en: 'Manager', az: 'Rəhbər' } },
  { key: 'specialist', label: { ru: 'Специалист', en: 'Specialist', az: 'Mütəxəssis' } },
  { key: 'worker', label: { ru: 'Рабочий', en: 'Worker', az: 'Fəhlə' } },
]

/** Руководители и специалисты — 15 критериев (таблица «Mütəxəssis-işçinin performansı»). */
const STAFF_CRITERIA: FormLabel[] = [
  { key: 'knowledge', label: { ru: 'Профессиональные знания', en: 'Professional knowledge', az: 'Peşə/iş bilikləri' } },
  { key: 'experience', label: { ru: 'Применение опыта работы', en: 'Applying experience', az: 'İş təcrübəsinin tətbiqi' } },
  { key: 'duties', label: { ru: 'Исполнение должностных обязанностей', en: 'Performing duties', az: 'Vəzifə öhdəliklərini yerinə yetirmə bacarığı' } },
  { key: 'productivity', label: { ru: 'Продуктивность', en: 'Productivity', az: 'Məhsuldarlıq' } },
  { key: 'timeliness', label: { ru: 'Своевременное выполнение работы', en: 'Timeliness', az: 'İşi vaxtında icra etmə bacarığı' } },
  { key: 'problems', label: { ru: 'Своевременно выявляет проблемы и сообщает о них', en: 'Spotting and reporting problems', az: 'Problemləri vaxtında aşkarlama və məlumat vermə bacarığı' } },
  { key: 'decisions', label: { ru: 'Верные решения в напряжённом темпе', en: 'Decisions under pressure', az: 'Gərgin iş tempində düzgün və çevik qərarvermə bacarığı' } },
  { key: 'planning', label: { ru: 'Планирование', en: 'Planning', az: 'Planlaşdırma bacarığı' } },
  { key: 'initiative', label: { ru: 'Инициативность', en: 'Initiative', az: 'Təşəbbüskarlıq' } },
  { key: 'software', label: { ru: 'MS Office и нужные программы', en: 'MS Office and required software', az: 'MS Office və/və ya tələb olunan proqram təminatından istifadə bacarığı' } },
  { key: 'responsibility', label: { ru: 'Ответственность', en: 'Responsibility', az: 'Məsuliyyətlilik' } },
  { key: 'communication', label: { ru: 'Корпоративное общение и поведение', en: 'Corporate communication', az: 'Korporativ ünsiyyət və davranış' } },
  { key: 'attendance', label: { ru: 'Посещаемость', en: 'Attendance', az: 'İşə davamiyyətlilik' } },
  { key: 'adaptability', label: { ru: 'Открытость изменениям', en: 'Openness to change', az: 'Yeniliklərə, dəyişikliklərə açıqlıq və adaptasiya' } },
  { key: 'safety', label: { ru: 'Соблюдение правил охраны труда', en: 'Occupational safety', az: 'Əməyin təhlükəsizliyi qaydalarına riayət' } },
]
/** Рабочие профессии — 5 критериев. */
const WORKER_CRITERIA: FormLabel[] = [
  { key: 'tools', label: { ru: 'Правильное обращение с инструментом', en: 'Proper use of tools', az: 'İş alətlərindən düzgün istifadə' } },
  { key: 'timeliness', label: { ru: 'Своевременное выполнение заданий', en: 'Timely tasks', az: 'Tapşırıqları vaxtında yerinə yetirmə' } },
  { key: 'discipline', label: { ru: 'Посещаемость и дисциплина', en: 'Attendance and discipline', az: 'Davamiyyət və nizam-intizam' } },
  { key: 'safety', label: { ru: 'Соблюдение техники безопасности', en: 'Safety rules', az: 'Texniki təhlükəsizlik qaydalarına riayət' } },
  { key: 'teamwork', label: { ru: 'Работа в команде', en: 'Teamwork', az: 'Komandada işləmə bacarığı' } },
]
export function criteriaFor(category: EmployerCategory): FormLabel[] {
  return category === 'worker' ? WORKER_CRITERIA : STAFF_CRITERIA
}
/** Шкала оценки формы: 5 — полностью соответствует, 1 — совсем не соответствует. */
export const SCORE_SCALE: { value: number; label: Localized }[] = [
  { value: 5, label: { ru: '5 — полностью соответствует', en: '5 — fully meets', az: '5 — tam uyğundur' } },
  { value: 4, label: { ru: '4 — соответствует, мелкие ошибки', en: '4 — meets, minor errors', az: '4 — uyğundur, kiçik səhvlərlə' } },
  { value: 3, label: { ru: '3 — частично, серьёзные пробелы', en: '3 — partly, serious gaps', az: '3 — qismən uyğundur' } },
  { value: 2, label: { ru: '2 — не соответствует', en: '2 — does not meet', az: '2 — uyğun deyil' } },
  { value: 1, label: { ru: '1 — совсем не соответствует', en: '1 — not at all', az: '1 — heç uyğun deyil' } },
]
/** Причины формы и подсказка статьи для HR (письмо У8): статью ставит HR на своём шаге. */
export const EMPLOYER_REASONS: { key: string; label: Localized; article: string | null }[] = [
  { key: 'reduction', label: { ru: 'Сокращение численности или штата', en: 'Headcount reduction', az: 'İşçi sayının azaldılması və ya ştatların ixtisarı' }, article: '70.b' },
  { key: 'grossViolation', label: { ru: 'Грубое нарушение должностной инструкции', en: 'Gross breach of job description', az: 'Vəzifə təlimatlarının kobud şəkildə pozulması' }, article: '70.ç' },
  { key: 'incompetence', label: { ru: 'Недостаточная компетентность для должности', en: 'Insufficient competence', az: 'Tutduğu vəzifəyə uyğun səriştəsinin az olması' }, article: '70.c' },
  { key: 'discipline', label: { ru: 'Нарушение правил дисциплины', en: 'Breach of discipline', az: 'Nizam-intizam qaydalarının pozulması' }, article: '70.ç' },
  { key: 'probation', label: { ru: 'Не выдержал испытательный срок', en: 'Failed probation', az: 'Sınaq müddətində işçinin özünü doğrultmaması' }, article: '70.d' },
  { key: 'nonPerformance', label: { ru: 'Неисполнение трудовых обязанностей', en: 'Non-performance of duties', az: 'İşçinin öz əmək funksiyasını yerinə yetirməməsi' }, article: '70.ç' },
  { key: 'other', label: { ru: 'Другое', en: 'Other', az: 'Digər' }, article: null },
]
/** Отметки проверки HR из формы — без всех четырёх сервер не даст согласовать. */
export const HR_CHECKS: { key: 'notified' | 'alternatives' | 'discussed' | 'lawful'; label: Localized }[] = [
  { key: 'notified', label: { ru: 'Сотрудника заранее известили об окончании договора или причине увольнения', en: 'The employee was notified in advance', az: 'İşçiyə əmək müqaviləsinin müddətinin başa çatması və ya çıxarma səbəbi haqda məlumat əvvəlcədən verilib' } },
  { key: 'alternatives', label: { ru: 'Рассмотрены подходящие вакансии и другие варианты сохранить сотрудника', en: 'Vacancies and alternatives were considered', az: 'İşçinin saxlanması məqsədilə uyğun boş vəzifə və ya digər alternativ imkanlar nəzərdən keçirilib' } },
  { key: 'discussed', label: { ru: 'Недостатки и претензии к работе заранее обсуждались с сотрудником', en: 'Shortcomings were discussed in advance', az: 'İşçiyə fəaliyyətində mövcud olan çatışmazlıqlarla bağlı əvvəlcədən məlumat verilib, məsələlər müzakirə olunub' } },
  { key: 'lawful', label: { ru: 'Причины увольнения и принятые меры соответствуют законодательству', en: 'Grounds and measures comply with the law', az: 'İşçinin işdən çıxarılması səbəbləri və görülmüş tədbirlər qanunvericiliyin tələblərinə uyğundur' } },
]

export interface EmployerTerminationInput {
  category?: EmployerCategory
  reasonCode?: string
  reasonOther?: string
  scores?: Record<string, number>
  notes?: string
  proposedDate?: string
}
export interface EmployerTerminationDraftInput extends EmployerTerminationInput {
  employeeId: string
  employmentId?: string
  targetEmploymentId?: string
}
export interface EmployerTerminationReferences {
  companyId: string
  source: string
  employee: { id: string; fullName: string; position: string | null; companyName: string | null }
  blockers: string[]
  canSubmit: boolean
}
export interface EmployerHrCheck {
  notified: boolean; alternatives: boolean; discussed: boolean; lawful: boolean
  rehire: 'yes' | 'no'; contractEndInfo: string | null; notes: string | null; byId: string; at: string
}
export interface EmployerTerminationPayload extends EmployerTerminationInput {
  employeeId: string
  employeeName?: string
  employeePosition?: string | null
  reason?: string
  scoreTotal?: number
  /** Раздел HR: статья и согласованная дата — их читает проводка в 1С. */
  terminationReasonId?: string
  terminationReasonName?: string
  terminationDate?: string
  hrCheck?: EmployerHrCheck
}
export interface TerminationHrInput {
  notified: boolean; alternatives: boolean; discussed: boolean; lawful: boolean
  terminationReasonId: string
  terminationDate: string
  rehire: 'yes' | 'no'
  contractEndInfo?: string
  notes?: string
}
export interface EmployerArticles {
  articles: { id: string; name: string; text: string }[]
  suggestedId: string | null
}

export function scoreSum(scores: Record<string, number> | undefined, category: EmployerCategory): number {
  return criteriaFor(category).reduce((sum, c) => sum + (Number(scores?.[c.key]) || 0), 0)
}
/** Все ли критерии категории оценены 1–5. */
export function scoresComplete(scores: Record<string, number> | undefined, category?: EmployerCategory): boolean {
  return !!category && criteriaFor(category).every((c) => {
    const v = scores?.[c.key]
    return Number.isInteger(v) && (v as number) >= 1 && (v as number) <= 5
  })
}
/** Подписи незаполненных полей для подсказки «Чтобы отправить, заполните: …» — по порядку формы. */
export function employerFormMissing(f: EmployerTerminationInput & { employeeId?: string }): Exclude<Localized, string>[] {
  const missing: Exclude<Localized, string>[] = []
  if (!f.employeeId) missing.push({ ru: 'Сотрудник', en: 'Employee', az: 'İşçi' })
  if (!f.category) missing.push({ ru: 'Категория', en: 'Category', az: 'Kateqoriya' })
  if (!f.reasonCode) missing.push({ ru: 'Причина', en: 'Reason', az: 'Səbəb' })
  else if (f.reasonCode === 'other' && !f.reasonOther?.trim()) missing.push({ ru: 'Пояснение причины', en: 'Reason details', az: 'Səbəbin izahı' })
  if (!scoresComplete(f.scores, f.category)) missing.push({ ru: 'Оценка по критериям', en: 'Evaluation', az: 'Qiymətləndirmə' })
  if (!f.proposedDate) missing.push({ ru: 'Предлагаемая дата', en: 'Proposed date', az: 'Təklif edilən tarix' })
  return missing
}
export function canEditEmployerDraft(card: EdoRequest, userId?: string): boolean {
  return !!userId && (card.initiator?.id ?? card.initiatorId) === userId && card.type === 'TERMINATION_EMPLOYER' &&
    card.status === 'DRAFT' && isPreparedTermination(card) &&
    !card.isSigned && !card.processInstanceId && !Object.keys(card.result ?? {}).length
}

/** Раздел HR в редактировании: строки пустые, пока HR их не заполнил; `rehire` '' — не ответил. */
export interface HrDraft {
  notified: boolean; alternatives: boolean; discussed: boolean; lawful: boolean
  terminationReasonId: string
  terminationDate: string
  rehire: '' | 'yes' | 'no'
  contractEndInfo: string
  notes: string
}
/** Согласованная дата предзаполняется предлагаемой: чаще всего HR её и подтверждает. */
export function hrDraftFrom(payload: Partial<EmployerTerminationPayload>): HrDraft {
  return {
    notified: false, alternatives: false, discussed: false, lawful: false,
    terminationReasonId: '',
    terminationDate: payload.terminationDate ?? payload.proposedDate ?? '',
    rehire: '',
    contractEndInfo: '',
    notes: '',
  }
}
/** Чего не хватает разделу HR для «Согласовать» — зеркало серверного hrCheckError. */
export function hrMissing(h: HrDraft): Exclude<Localized, string>[] {
  const missing: Exclude<Localized, string>[] = []
  if (!HR_CHECKS.every((c) => h[c.key])) missing.push({ ru: 'Отметки проверки HR', en: 'HR checks', az: 'HR yoxlaması' })
  if (!h.terminationReasonId) missing.push({ ru: 'Статья ТК АР', en: 'Labour Code article', az: 'ƏM maddəsi' })
  if (!h.terminationDate) missing.push({ ru: 'Согласованная дата', en: 'Agreed date', az: 'Razılaşdırılmış tarix' })
  if (!h.rehire) missing.push({ ru: '«Взяли бы снова»', en: '"Would rehire"', az: '«Yenidən işə götürərdinizmi»' })
  return missing
}
export function hrInput(h: HrDraft): TerminationHrInput {
  return {
    notified: h.notified, alternatives: h.alternatives, discussed: h.discussed, lawful: h.lawful,
    terminationReasonId: h.terminationReasonId,
    terminationDate: h.terminationDate,
    rehire: h.rehire as 'yes' | 'no',
    contractEndInfo: h.contractEndInfo.trim() || undefined,
    notes: h.notes.trim() || undefined,
  }
}
